import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { 
  Monitor, 
  CheckCircle,
  Star,
  Globe,
  Settings,
  ArrowRight,

} from 'lucide-react';
import { Link } from 'react-router-dom';
import { fadeInUp } from '../utils/cn';

const Companies = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="section-padding solid-bg text-white relative overflow-hidden">
      
      
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
          style={{
            backgroundImage: `url('/divisions/Commercial - Electrical/the-assembly-of-the-electrical-panel-electrician-2023-11-27-04-53-19-utc.jpg')`
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/60 to-transparent" />
        
        {/* Floating Energy Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 bg-global-green rounded-full opacity-40 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${4 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        <div className="container-max relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-12 items-center min-h-[60vh]">
            {/* Left Content */}
            <motion.div
              className="space-y-8"
              {...fadeInUp}
            >
              
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center">
                Commercial Partners of 
                <span className="text-white"> Global Technology Company</span>
              </h1>
              
              <p className="text-xl leading-relaxed opacity-90">
                Partnering with industry leaders to deliver world-class commercial solutions 
              and cutting-edge technology across diverse sectors.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" variant="secondary" className="group">
                    Get Quote
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/divisions/commercial">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-charcoal">
                    Back to Commercial Partners
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="section-padding bg-gray-50 text-black">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-global-green">
              Companies Global Technology Represent
            </h2>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Partnering with industry leaders to deliver world-class commercial solutions.
            </p>
          </motion.div>

          {/* Arbiter Systems Inc. Section */}
          <motion.div
            className="bg-white rounded-2xl p-8 text-global-green mb-12 shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4 flex items-center">
                  <Monitor className="w-8 h-8 mr-3 " />
                  Arbiter Systems Inc.
                </h3>
                <h4 className="text-xl font-semibold mb-4 ">
                  Enabling Tomorrow's Technology Today
                </h4>
                <p className="text-black mb-6 leading-relaxed">
                  Arbiter Systems® Inc. manufactures GPS based precision time clocks, synchronized power measurement 
                  and power calibration products, designed to support industries where precision timing is mission critical. 
                  Applications include synchrophasor measurements, synchronized power measurements, computer network time 
                  synchronization (via NTP or PTP), event timing and other electric utility support.
                </p>
                <p className="text-black mb-6 leading-relaxed">
                  These products enable the power distribution industry to deliver safe, reliable and economical power 
                  to end users globally. Designed, assembled and serviced by Arbiter, these solutions provide the backbone 
                  for professional monitoring, measurement and analysis of power system performance. Pioneers in GPS 
                  satellite-based precision timing products since 1976, Arbiter continues to push the market forward 
                  by introducing synchrophasor support into its product line.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center">
                    <Star className="w-5 h-5 text-metallic-gold mr-2" />
                    <span className="text-sm">Since 1976</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 text-metallic-gold mr-2" />
                    <span className="text-sm">GPS Precision Timing</span>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-global-green">Product Range</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        GPS Precision Time Clocks
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Synchronized Power Measurement
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Power Calibration Products
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Phasor Measurement Units (PMU)
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-global-green">Applications</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Synchrophasor Measurements
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Network Time Synchronization
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Electric Utility Support
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Power System Analysis
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="border-t border-white/20 pt-6">
                  <p className="text-sm leading-relaxed opacity-90 text-black">
                    Synchrophasors are used to measure voltages and currents across diverse power grid locations 
                    providing synchronized comparison of two quantities in real time, and have become a key tool 
                    in the management of power quality.
                  </p>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-lg font-semibold mb-2 text-global-green">Key Features</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <span className="bg-global-green/10 text-black rounded px-2 py-1">GPS Based</span>
                    <span className="bg-global-green/10 text-black rounded px-2 py-1">Precision Timing</span>
                    <span className="bg-global-green/10 text-black rounded px-2 py-1">NTP/PTP Support</span>
                    <span className="bg-global-green/10 text-black rounded px-2 py-1">Lifetime Warranty</span>
                    <span className="bg-global-green/10 text-black rounded px-2 py-1">Custom Solutions</span>
                    <span className="bg-global-green/10 text-black rounded px-2 py-1">Technical Support</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Dalian Insulators Section */}
          <motion.div
            className="bg-white rounded-2xl p-8 text-global-green shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4 flex items-center">
                  <Settings className="w-8 h-8 mr-3" />
                  Dalian Insulators
                </h3>
                <h4 className="text-xl font-semibold mb-4 ">
                  We Power the World
                </h4>
                <p className="text-black mb-6 leading-relaxed">
                  Dalian electric porcelain Group Co., Ltd., founded in 1915, is a high-tech enterprise specializing 
                  in the R & D, production and sales of insulator products. The company has three subsidiaries and 
                  was listed on the small and medium-sized board of Shenzhen Stock Exchange in August 2011.
                </p>
                <p className="text-black mb-6 leading-relaxed">
                  The company has complete process equipment, advanced testing means, strict implementation of 
                  International Electrotechnical Commission (IEC) standards and Chinese, American, British and 
                  other standards, and perfect quality assurance system. The company attaches importance to 
                  technological innovation and has won numerous national and international awards.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center">
                    <Star className="w-5 h-5 text-metallic-gold mr-2" />
                    <span className="text-sm">Since 1915</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 text-metallic-gold mr-2" />
                    <span className="text-sm">Listed Company</span>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-black">Product Range</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        AC/DC Suspension Insulators
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Composite Insulators
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Post Insulators
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        High-Voltage Porcelain Sleeves
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-black">Voltage Range</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        10kV ~ 1000kV AC
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        ±500kV ~ ±1100kV DC
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        40kN ~ 840kN Mechanical
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Power Station Applications
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="border-t border-global-green/20 pt-6">
                  <p className="text-sm leading-relaxed opacity-90 text-black">
                    With over 100 years of experience, Dalian Insulators combines traditional craftsmanship 
                    with modern technology to deliver world-class insulator solutions for power transmission 
                    and distribution systems.
                  </p>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-lg font-semibold mb-2 text-black">Awards & Recognition</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <span className="bg-global-green/10 rounded px-2 py-1 text-global-green">National Quality Gold Award</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-global-green">Science & Technology Progress Award</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-global-green">Manufacturing Champion</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-global-green">Liaoning Gazelle Enterprise</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-global-green">CNAS Laboratory Accreditation</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-global-green">IEC Standards Compliance</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Companies;